/**
 * One in-flight WORKSPACE_USER write at a time, per user and workspace.
 * The mutation replaces the whole document. Each field keeps the value this
 * client wrote until props echo it.
 * Team history unions with props. Pins, team canvas, Home, and header search
 * use the pending value as written. A cleared search list is kept as written.
 * A failed write restores the previous pending values.
 * A field write still waiting behind the in-flight save is shown immediately.
 */

import { readHomeTeamViews, unionHomeTeamViews } from "./homeTeamViews";

type WriteScope = {
  workspaceId: string;
  userId: string;
};

type HeaderSearchPending = {
  value: user_prefs.HeaderSearchPreference;
  /** Empty recentSearches was an explicit clear, not an absent list. */
  clearedSearches: boolean;
};

type Pending = {
  homeV2: { enabled: boolean } | null;
  headerSearch: HeaderSearchPending | null;
  homeTeamViews: user_prefs.HomeTeamView[];
  pinnedItems: user_prefs.PinnedItems | null;
  teamCanvasViews: user_prefs.TeamCanvasViews | null;
  teamCanvasViewCollections: user_prefs.TeamCanvasViewCollections | null;
};

type PreviewApply = (
  latest: user_prefs.Preferences | null | undefined
) => user_prefs.Preferences | null;

type Bucket = {
  chain: Promise<void>;
  pending: Pending;
  /** Field writes not yet folded into pending. Shown before their send starts. */
  queued: PreviewApply[];
};

type OwnedField = "homeV2" | "headerSearch" | "homeTeamViews" | "document";

const buckets = new Map<string, Bucket>();
const listeners = new Set<() => void>();
let generation = 0;
let snapshotVersion = 0;

const notifyWriteQueue = () => {
  snapshotVersion += 1;
  listeners.forEach((listener) => listener());
};

const scopeKey = (scope: WriteScope) => `${scope.userId}\0${scope.workspaceId}`;

const bucketFor = (key: string): Bucket => {
  const existing = buckets.get(key);
  if (existing) {
    return existing;
  }
  const created: Bucket = {
    chain: Promise.resolve(),
    pending: emptyPending(),
    queued: [],
  };
  buckets.set(key, created);
  return created;
};

const emptyPending = (): Pending => ({
  homeV2: null,
  headerSearch: null,
  homeTeamViews: [],
  pinnedItems: null,
  teamCanvasViews: null,
  teamCanvasViewCollections: null,
});

const sameJson = (left: unknown, right: unknown) =>
  JSON.stringify(left) === JSON.stringify(right);

const cloneRows = (
  rows: readonly user_prefs.HomeTeamView[]
): user_prefs.HomeTeamView[] =>
  rows.map((row) => ({
    teamId: row.teamId,
    name: row.name,
    breadcrumb: [...row.breadcrumb],
    days: [...row.days],
  }));

const rowEchoed = (
  propsRows: readonly user_prefs.HomeTeamView[],
  row: user_prefs.HomeTeamView
): boolean => {
  const match = propsRows.find((view) => view.teamId === row.teamId);
  if (!match) {
    return false;
  }
  return (
    match.name === row.name &&
    match.breadcrumb.join("\0") === row.breadcrumb.join("\0") &&
    match.days.join(",") === row.days.join(",")
  );
};

const cloneList = <T>(
  value: readonly T[] | null,
  mapItem: (item: T) => T
): T[] | null => (value === null ? null : value.map(mapItem));

const clonePending = (pending: Pending): Pending => ({
  homeV2: pending.homeV2 ? { enabled: pending.homeV2.enabled } : null,
  headerSearch: pending.headerSearch
    ? {
        value: pending.headerSearch.value,
        clearedSearches: pending.headerSearch.clearedSearches,
      }
    : null,
  homeTeamViews: cloneRows(pending.homeTeamViews),
  pinnedItems: cloneList(pending.pinnedItems, (item) => ({ ...item })),
  teamCanvasViews: cloneList(pending.teamCanvasViews, (view) => ({
    ...view,
    teamIds: [...view.teamIds],
  })),
  teamCanvasViewCollections: cloneList(
    pending.teamCanvasViewCollections,
    (collection) => ({
      ...collection,
      viewIds: [...collection.viewIds],
    })
  ),
});

const echoedValue = <T>(
  pendingValue: T | null,
  propsValue: T | undefined
): T | null => {
  if (pendingValue === null) {
    return null;
  }
  return sameJson(pendingValue, propsValue) ? null : pendingValue;
};

/** Drop a pending field only when props repeat that write exactly. */
const releaseEchoed = (
  pending: Pending,
  props: user_prefs.Preferences | null | undefined
): Pending => {
  const propsRows = readHomeTeamViews(props?.homeTeamViews);
  return {
    homeV2:
      pending.homeV2 && props?.homeV2?.enabled === pending.homeV2.enabled
        ? null
        : pending.homeV2,
    headerSearch:
      pending.headerSearch &&
      sameJson(props?.headerSearch, pending.headerSearch.value)
        ? null
        : pending.headerSearch,
    homeTeamViews: pending.homeTeamViews.filter(
      (row) => !rowEchoed(propsRows, row)
    ),
    pinnedItems: echoedValue(pending.pinnedItems, props?.pinnedItems),
    teamCanvasViews: echoedValue(
      pending.teamCanvasViews,
      props?.teamCanvasViews
    ),
    teamCanvasViewCollections: echoedValue(
      pending.teamCanvasViewCollections,
      props?.teamCanvasViewCollections
    ),
  };
};

const hasPending = (pending: Pending) =>
  pending.homeV2 !== null ||
  pending.headerSearch !== null ||
  pending.homeTeamViews.length > 0 ||
  pending.pinnedItems !== null ||
  pending.teamCanvasViews !== null ||
  pending.teamCanvasViewCollections !== null;

/**
 * Props, plus writes this client sent that the server has not echoed.
 * Team rows union. Pins and team canvas use the pending value as written.
 */
const overlayPending = (
  pending: Pending,
  props: user_prefs.Preferences | null | undefined
): user_prefs.Preferences | null | undefined => {
  if (!hasPending(pending)) {
    return props;
  }
  return {
    ...(props ?? {}),
    ...(pending.homeV2
      ? {
          homeV2: {
            ...(props?.homeV2 ?? {}),
            enabled: pending.homeV2.enabled,
          },
        }
      : {}),
    ...(pending.headerSearch
      ? { headerSearch: pending.headerSearch.value }
      : {}),
    ...(pending.homeTeamViews.length > 0
      ? {
          homeTeamViews: unionHomeTeamViews(
            props?.homeTeamViews,
            pending.homeTeamViews
          ),
        }
      : {}),
    ...(pending.pinnedItems !== null
      ? { pinnedItems: pending.pinnedItems }
      : {}),
    ...(pending.teamCanvasViews !== null
      ? { teamCanvasViews: pending.teamCanvasViews }
      : {}),
    ...(pending.teamCanvasViewCollections !== null
      ? { teamCanvasViewCollections: pending.teamCanvasViewCollections }
      : {}),
  };
};

const pendingTeamRows = (
  props: user_prefs.Preferences | null | undefined,
  next: user_prefs.Preferences
): user_prefs.HomeTeamView[] => {
  const propsRows = readHomeTeamViews(props?.homeTeamViews);
  return readHomeTeamViews(next.homeTeamViews).filter(
    (row) => !rowEchoed(propsRows, row)
  );
};

const pendingHomeV2 = (
  props: user_prefs.Preferences | null | undefined,
  next: user_prefs.Preferences
): Pending["homeV2"] => {
  const enabled = next.homeV2?.enabled;
  if (typeof enabled !== "boolean" || props?.homeV2?.enabled === enabled) {
    return null;
  }
  return { enabled };
};

const searchCount = (
  value: user_prefs.HeaderSearchPreference | null | undefined
) => value?.recentSearches?.length ?? 0;

const pendingHeaderSearch = (
  released: Pending,
  props: user_prefs.Preferences | null | undefined,
  next: user_prefs.Preferences
): HeaderSearchPending | null => {
  const value = next.headerSearch ?? {
    recentSearches: [],
    recentlyViewedPeople: [],
  };
  if (sameJson(props?.headerSearch, value)) {
    return null;
  }
  const clearedSearches =
    searchCount(value) === 0 &&
    (searchCount(props?.headerSearch) > 0 ||
      searchCount(released.headerSearch?.value) > 0 ||
      released.headerSearch?.clearedSearches === true);
  return { value, clearedSearches };
};

const rememberField = <T>(
  included: boolean,
  nextValue: T | undefined,
  propsValue: T | undefined,
  released: T | null
): T | null => {
  if (!included) {
    return released;
  }
  if (nextValue === undefined || sameJson(nextValue, propsValue)) {
    return null;
  }
  return nextValue;
};

const remember = (
  released: Pending,
  props: user_prefs.Preferences | null | undefined,
  next: user_prefs.Preferences
): Pending => ({
  homeV2:
    next.homeV2 === undefined ? released.homeV2 : pendingHomeV2(props, next),
  headerSearch:
    next.headerSearch === undefined
      ? released.headerSearch
      : pendingHeaderSearch(released, props, next),
  homeTeamViews:
    next.homeTeamViews === undefined
      ? released.homeTeamViews
      : pendingTeamRows(props, next),
  pinnedItems: rememberField(
    next.pinnedItems !== undefined,
    next.pinnedItems,
    props?.pinnedItems,
    released.pinnedItems
  ),
  teamCanvasViews: rememberField(
    next.teamCanvasViews !== undefined,
    next.teamCanvasViews,
    props?.teamCanvasViews,
    released.teamCanvasViews
  ),
  teamCanvasViewCollections: rememberField(
    next.teamCanvasViewCollections !== undefined,
    next.teamCanvasViewCollections,
    props?.teamCanvasViewCollections,
    released.teamCanvasViewCollections
  ),
});

/**
 * Caller snapshot, plus pending fields that snapshot does not already have.
 * A partial write such as `{ language }` is left unchanged when nothing is pending.
 */
const mergeCaller = (
  pending: Pending,
  props: user_prefs.Preferences | null | undefined,
  caller: user_prefs.Preferences
): user_prefs.Preferences => {
  let next: user_prefs.Preferences = { ...caller };
  const unioned = unionHomeTeamViews(
    unionHomeTeamViews(props?.homeTeamViews, pending.homeTeamViews),
    caller.homeTeamViews
  );
  if (
    unioned.length > 0 &&
    !sameJson(unioned, readHomeTeamViews(caller.homeTeamViews))
  ) {
    next = { ...next, homeTeamViews: unioned };
  }
  if (
    pending.pinnedItems !== null &&
    (caller.pinnedItems === undefined ||
      sameJson(caller.pinnedItems, props?.pinnedItems))
  ) {
    next = { ...next, pinnedItems: pending.pinnedItems };
  }
  if (
    pending.teamCanvasViews !== null &&
    (caller.teamCanvasViews === undefined ||
      sameJson(caller.teamCanvasViews, props?.teamCanvasViews))
  ) {
    next = { ...next, teamCanvasViews: pending.teamCanvasViews };
  }
  if (
    pending.teamCanvasViewCollections !== null &&
    (caller.teamCanvasViewCollections === undefined ||
      sameJson(
        caller.teamCanvasViewCollections,
        props?.teamCanvasViewCollections
      ))
  ) {
    next = {
      ...next,
      teamCanvasViewCollections: pending.teamCanvasViewCollections,
    };
  }
  if (
    pending.homeV2 !== null &&
    (caller.homeV2 === undefined ||
      sameJson(caller.homeV2?.enabled, props?.homeV2?.enabled))
  ) {
    next = {
      ...next,
      homeV2: {
        ...(caller.homeV2 ?? props?.homeV2 ?? {}),
        enabled: pending.homeV2.enabled,
      },
    };
  }
  if (
    pending.headerSearch !== null &&
    (caller.headerSearch === undefined ||
      sameJson(caller.headerSearch, props?.headerSearch))
  ) {
    next = { ...next, headerSearch: pending.headerSearch.value };
  }
  return next;
};

/** Re-render when a queued write changes the overlay the UI should show. */
export const subscribeWorkspaceUserWriteQueue = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getWorkspaceUserWriteQueueVersion = () => snapshotVersion;

/**
 * Props plus this client's writes, including field writes still waiting
 * behind the in-flight save. Echoed fields fall back to props.
 */
export const readWorkspaceUserPreferencesOverlay = (
  scope: WriteScope,
  props: user_prefs.Preferences | null | undefined
): user_prefs.Preferences | null | undefined => {
  const bucket = buckets.get(scopeKey(scope));
  if (!bucket || (!hasPending(bucket.pending) && bucket.queued.length === 0)) {
    return props;
  }
  let view = overlayPending(releaseEchoed(bucket.pending, props), props);
  for (const apply of bucket.queued) {
    const next = apply(view);
    if (next) {
      view = next;
    }
  }
  return view;
};

export const resetWorkspaceUserWriteQueueForTests = () => {
  generation += 1;
  buckets.clear();
  notifyWriteQueue();
};

const enqueue = ({
  scope,
  readScope,
  field,
  readProps,
  apply,
  previewApply,
  send,
  isLoading,
}: {
  scope: { workspaceId: string; userId: string };
  readScope: () => { workspaceId: string; userId: string };
  field: OwnedField;
  readProps: () => user_prefs.Preferences | null | undefined;
  apply: (
    latest: user_prefs.Preferences | null | undefined,
    props: user_prefs.Preferences | null | undefined,
    pending: Pending
  ) => user_prefs.Preferences | null;
  /** Shown in the overlay as soon as this write is queued. */
  previewApply?: PreviewApply;
  send: (preferences: user_prefs.Preferences) => Promise<boolean>;
  isLoading?: () => boolean;
}): Promise<boolean> => {
  switch (field) {
    case "homeV2":
    case "headerSearch":
    case "homeTeamViews":
    case "document":
      break;
    default: {
      const unhandled: never = field;
      return Promise.resolve(unhandled);
    }
  }
  const key = scopeKey(scope);
  const bucket = bucketFor(key);
  const ticket = generation;
  if (previewApply) {
    bucket.queued.push(previewApply);
    notifyWriteQueue();
  }
  const task = bucket.chain.then(async () => {
    if (previewApply) {
      bucket.queued.shift();
    }
    if (ticket !== generation || scopeKey(readScope()) !== key) {
      if (previewApply) {
        notifyWriteQueue();
      }
      return false;
    }
    if (isLoading?.()) {
      if (previewApply) {
        notifyWriteQueue();
      }
      return false;
    }
    const props = readProps();
    const previous = clonePending(bucket.pending);
    const released = releaseEchoed(previous, props);
    const latest = overlayPending(released, props);
    const next = apply(latest, props, released);
    if (!next) {
      if (previewApply) {
        notifyWriteQueue();
      }
      return true;
    }
    bucket.pending = remember(released, props, next);
    notifyWriteQueue();
    try {
      const saved = await send(next);
      if (ticket !== generation) {
        return false;
      }
      if (!saved) {
        bucket.pending = previous;
        notifyWriteQueue();
        return false;
      }
      return true;
    } catch (error) {
      if (ticket === generation) {
        bucket.pending = previous;
        notifyWriteQueue();
      }
      throw error;
    }
  });
  bucket.chain = task.then(
    () => undefined,
    () => undefined
  );
  return task;
};

export const enqueueWorkspaceUserWrite = ({
  scope,
  readScope,
  field,
  readProps,
  apply,
  send,
  isLoading,
}: {
  scope: { workspaceId: string; userId: string };
  readScope: () => { workspaceId: string; userId: string };
  field: "homeV2" | "headerSearch" | "homeTeamViews";
  readProps: () => user_prefs.Preferences | null | undefined;
  /** Null means the field did not change, so nothing is sent. */
  apply: (
    latest: user_prefs.Preferences | null | undefined
  ) => user_prefs.Preferences | null;
  send: (preferences: user_prefs.Preferences) => Promise<boolean>;
  isLoading?: () => boolean;
}): Promise<boolean> =>
  enqueue({
    scope,
    readScope,
    field,
    readProps,
    apply: (latest) => apply(latest),
    previewApply: apply,
    send,
    ...(isLoading ? { isLoading } : {}),
  });

/** A pin or team-canvas save. Keeps team visits and other in-flight fields. */
export const enqueueWorkspaceUserDocumentWrite = ({
  scope,
  readScope,
  readProps,
  preferences,
  send,
}: {
  scope: { workspaceId: string; userId: string };
  readScope: () => { workspaceId: string; userId: string };
  readProps: () => user_prefs.Preferences | null | undefined;
  preferences: user_prefs.Preferences;
  send: (preferences: user_prefs.Preferences) => Promise<boolean>;
}): Promise<boolean> =>
  enqueue({
    scope,
    readScope,
    field: "document",
    readProps,
    apply: (_latest, props, pending) =>
      mergeCaller(pending, props, preferences),
    send,
  });
